// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useEffect, useState } from 'react'
import { useLocales } from 'react-localized'
import { RouteComponentProps } from 'react-router'
import { IonButton, IonButtons, IonFooter, IonToolbar } from '@ionic/react'
import { ProcurementClient, TrackableDocument } from '../api'
import { ScreenSizeConditional, SectionPage, SectionContainer, useSession } from '../lib'
import { AcceptDocument, AccessCodeInput, LoadingPurchaseOrderDetails, PurchaseOrderDetails } from '../components/'


interface RegisterTrackingCodePageProps extends RouteComponentProps<{id: string}> {}


const RegisterTrackingCodePage: React.FC<RegisterTrackingCodePageProps> = ({
  match
}) => {
  const params = new URLSearchParams(window.location.search)
  const cacheKey = `purchaseorder:${match.params.id}`
  const {gettext} = useLocales()
  const client = new ProcurementClient()
  const code = localStorage.getItem(cacheKey) || params.get("code")
  const [document, setDocument] = useState<TrackableDocument>(null!)
  const [loading, setLoading] = useState<boolean>(false)
  const [ready, setReady] = useState<boolean>(false)
  const {error, setError, ...session} = useSession()

  useEffect(() => {
    if (!!code) {
      client.getTrackableDocument(match.params.id, code)
        .then(setDocument)
        .catch(() => setError(true))
    } else {
      setReady(true)
    }
  }, [code])

  useEffect(() => {
    if (document !== null) { setReady(true) }
  }, [document])

  const onSubmit: (code: string) => Promise<boolean> = async (code) => {
    setLoading(true)
    try {
      setDocument(await client.getTrackableDocument(match.params.id, code))
      localStorage.setItem(cacheKey, code)
      return true
    } catch (exc: any) {
      if (exc.code !== "INVALID_CREDENTIAL") {
        setError(true)
      }
      console.log("Caught fatal", exc)
      return false
    } finally {
      setLoading(false)
      setReady(true)
    }
  }

  return (
    <SectionPage>
      <SectionContainer
        anonymous
        contextPanel={(document === null) ? LoadingPurchaseOrderDetails : PurchaseOrderDetails}
        contextPanelProps={{document: document}}
        loading={loading}
        ready={session.ready}
        title={gettext`Order #${match.params.id} confirmation`}
      >
        {(!!code)
          ? (<AcceptDocument document={document}/>)
          : (<AccessCodeInput
              continueLabel={gettext("Review and confirm order")}
              onSubmit={onSubmit}
            />)
        }
      </SectionContainer>
      <ScreenSizeConditional>
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton>{gettext("View products")}</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </ScreenSizeConditional>
    </SectionPage>
  )
}

export { RegisterTrackingCodePage }
